import { motion } from 'framer-motion';
import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

function AnimatedNumber({ target, suffix = '', prefix = '', decimals = 0, duration = 2000 }: { target: number; suffix?: string; prefix?: string; decimals?: number; duration?: number }) {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started) {
          setStarted(true);
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    const steps = 60;
    const increment = target / steps;
    let current = 0;
    const interval = setInterval(() => {
      current += increment;
      if (current >= target) {
        setCount(target);
        clearInterval(interval);
      } else {
        setCount(decimals > 0 ? parseFloat(current.toFixed(decimals)) : Math.floor(current));
      }
    }, duration / steps);
    return () => clearInterval(interval);
  }, [started, target, duration, decimals]);

  return (
    <span ref={ref}>
      {prefix}{decimals > 0 ? count.toFixed(decimals) : count.toLocaleString()}{suffix}
    </span>
  );
}

const yearlyGrowthData = [
  { year: '2019', aadt: 168, visitors: 32.2, color: 'bg-gray-600' },
  { year: '2020', aadt: 112, visitors: 19.0, color: 'bg-gray-700' },
  { year: '2021', aadt: 145, visitors: 32.2, color: 'bg-gray-600' },
  { year: '2022', aadt: 172, visitors: 38.8, color: 'bg-amber-700' },
  { year: '2023', aadt: 189, visitors: 40.8, color: 'bg-amber-600' },
  { year: '2024', aadt: 205, visitors: 42.7, color: 'bg-amber-500' },
];

const cpmComparisonData = [
  { media: 'Google Ads (Search)', cpm: 38.40, barPercent: 100, color: 'bg-red-500/70', source: 'WordStream 2024' },
  { media: 'Meta / Instagram', cpm: 14.90, barPercent: 39, color: 'bg-red-400/60', source: 'Statista 2024' },
  { media: 'YouTube (Video)', cpm: 9.68, barPercent: 25, color: 'bg-orange-400/50', source: 'Influencer Marketing Hub' },
  { media: 'OOH (US Average)', cpm: 5.20, barPercent: 14, color: 'bg-amber-500/60', source: 'OAAA 2024' },
  { media: 'I-15 Billboard', cpm: 2.80, barPercent: 7, color: 'bg-emerald-500', source: 'Internal Calculation (AADT-based)', highlight: true },
];

const eventPeakData = [
  { name: 'CES', period: 'January', surge: '+280%', attendees: '138,000', note: 'World\'s Largest Tech Show' },
  { name: 'Super Bowl LVIII', period: 'February', surge: '+340%', attendees: '120,000+', note: 'First Las Vegas Hosting 2024' },
  { name: 'F1 Las Vegas GP', period: 'November', surge: '+310%', attendees: '315,000', note: 'Street Circuit on the Strip' },
  { name: 'NFR Finals', period: 'December', surge: '+180%', attendees: '275,000', note: 'Largest Rodeo Championship' },
];

const hourlyData = [
  { label: '0-4am', value: 68, percent: 45 },
  { label: '4-8am', value: 95, percent: 63 },
  { label: '8-12pm', value: 142, percent: 95 },
  { label: '12-4pm', value: 150, percent: 100 },
  { label: '4-8pm', value: 148, percent: 99 },
  { label: '8-12am', value: 135, percent: 90 },
];

export default function WhySection() {
  return (
    <section className="relative py-28 px-6 bg-gradient-to-b from-gray-950 to-gray-900 overflow-hidden">
      {/* Subtle grid background */}
      <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)', backgroundSize: '60px 60px' }}></div>

      <div className="relative max-w-7xl mx-auto">
        {/* Header - IR Document Style */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-1 h-8 bg-amber-500 rounded-full"></div>
            <p className="text-amber-500 font-semibold text-xs tracking-[0.2em] uppercase">Market Intelligence — Billboard Asset Overview</p>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-3 leading-tight">
            Premium Out-of-Home <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-500">Media Assets</span>
          </h2>
          <p className="text-gray-400 text-sm max-w-3xl leading-relaxed">
            Digital billboards along I-15 in Las Vegas deliver over <strong className="text-gray-200">74 million annual impressions</strong> per face. This section provides traffic data, visitor statistics, and cost efficiency metrics based on third-party sources for advertisers, investors, and property owners.
          </p>
        </motion.div>

        {/* Disclaimer bar */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="mb-10 flex items-center gap-2 px-4 py-2.5 bg-gray-800/50 border border-gray-700/50 rounded-md"
        >
          <div className="w-5 h-5 flex items-center justify-center shrink-0">
            <i className="ri-information-line text-gray-500 text-sm"></i>
          </div>
          <p className="text-gray-500 text-xs leading-relaxed">
            Data sourced from Nevada DOT, LVCVA (Las Vegas Convention and Visitors Authority), OAAA (Out of Home Advertising Association of America), and industry reports. Internal calculations are clearly labeled.
          </p>
        </motion.div>

        {/* ===== SECTION 1: Primary KPIs ===== */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
          className="mb-3"
        >
          <p className="text-gray-600 text-[10px] font-semibold tracking-[0.15em] uppercase">Section 01 — Key Performance Indicators</p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
          {/* AADT */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="bg-gray-900/80 border border-gray-800 rounded-lg p-7 hover:border-amber-500/30 transition-colors duration-300"
          >
            <div className="flex items-center gap-2 mb-4">
              <div className="w-7 h-7 flex items-center justify-center rounded-md bg-amber-500/10">
                <i className="ri-road-map-line text-amber-500 text-base"></i>
              </div>
              <span className="text-gray-500 text-[10px] font-semibold tracking-wider uppercase">AADT (Avg. Daily Traffic)</span>
            </div>
            <div className="text-5xl font-black text-white mb-1.5 tracking-tight">
              <AnimatedNumber target={205} suffix="K" />
            </div>
            <p className="text-gray-500 text-xs mb-1">vehicles/day — I-15 Las Vegas Corridor</p>
            <p className="text-gray-600 text-[10px]">Source: Nevada DOT 2024</p>
            <div className="mt-4 pt-4 border-t border-gray-800">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <span className="text-emerald-400 text-xs font-semibold flex items-center gap-0.5">
                    <i className="ri-arrow-up-line text-[10px]"></i>+8.5%
                  </span>
                  <span className="text-gray-600 text-[10px]">YoY</span>
                </div>
                <span className="text-gray-600 text-[10px]">vs 2019: +5.2%</span>
              </div>
            </div>
          </motion.div>

          {/* Annual Visitors */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="bg-gray-900/80 border border-gray-800 rounded-lg p-7 hover:border-amber-500/30 transition-colors duration-300"
          >
            <div className="flex items-center gap-2 mb-4">
              <div className="w-7 h-7 flex items-center justify-center rounded-md bg-amber-500/10">
                <i className="ri-user-star-line text-amber-500 text-base"></i>
              </div>
              <span className="text-gray-500 text-[10px] font-semibold tracking-wider uppercase">Annual Visitors</span>
            </div>
            <div className="text-5xl font-black text-white mb-1.5 tracking-tight">
              <AnimatedNumber target={42} suffix=".7M" />
            </div>
            <p className="text-gray-500 text-xs mb-1">annual visitors — 2024</p>
            <p className="text-gray-600 text-[10px]">Source: LVCVA 2024</p>
            <div className="mt-4 pt-4 border-t border-gray-800">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <span className="text-emerald-400 text-xs font-semibold flex items-center gap-0.5">
                    <i className="ri-arrow-up-line text-[10px]"></i>+4.7%
                  </span>
                  <span className="text-gray-600 text-[10px]">YoY</span>
                </div>
                <span className="text-gray-600 text-[10px]">vs 2019: +5.2%</span>
              </div>
            </div>
          </motion.div>

          {/* Estimated Annual Impressions */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="bg-gray-900/80 border border-gray-800 rounded-lg p-7 hover:border-amber-500/30 transition-colors duration-300"
          >
            <div className="flex items-center gap-2 mb-4">
              <div className="w-7 h-7 flex items-center justify-center rounded-md bg-amber-500/10">
                <i className="ri-eye-line text-amber-500 text-base"></i>
              </div>
              <span className="text-gray-500 text-[10px] font-semibold tracking-wider uppercase">Est. Annual Impressions</span>
            </div>
            <div className="text-5xl font-black text-white mb-1.5 tracking-tight">
              <AnimatedNumber target={74} suffix=".8M" />
            </div>
            <p className="text-gray-500 text-xs mb-1">impressions/year — Per Billboard Face</p>
            <p className="text-gray-600 text-[10px]">Internal Calculation (AADT × DEC Factor)</p>
            <div className="mt-4 pt-4 border-t border-gray-800">
              <div className="flex items-center justify-between">
                <span className="text-gray-500 text-[10px]">Visibility Factor: 1.0 (Geopath Standard)</span>
                <span className="text-gray-600 text-[10px]">DEC Compliant</span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* ===== CTA Section ===== */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-gradient-to-r from-gray-800/80 to-gray-900/80 border border-gray-700/50 rounded-lg p-8 md:p-10 mb-10"
        >
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div className="max-w-xl">
              <p className="text-amber-500 text-[10px] font-semibold tracking-[0.15em] uppercase mb-2">Next Step</p>
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">
                Explore Detailed Performance Data
              </h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                Access comprehensive materials for advertisers and investors, or calculate potential returns with our ROI simulator. Schedule a consultation for personalized insights.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 shrink-0">
              <Link
                to="/en/simulator"
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-black font-semibold text-sm rounded-full hover:shadow-[0_0_20px_rgba(245,158,11,0.4)] transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-calculator-line text-base"></i>
                ROI Calculator
                <i className="ri-arrow-right-line text-base"></i>
              </Link>
              <Link
                to="/en/advertise"
                className="inline-flex items-center gap-2 px-6 py-3 bg-transparent border border-gray-600 text-gray-300 font-semibold text-sm rounded-full hover:border-amber-500/50 hover:text-white transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-file-text-line text-base"></i>
                Advertising Plans
              </Link>
              <Link
                to="/en/partnership"
                className="inline-flex items-center gap-2 px-6 py-3 bg-transparent border border-gray-600 text-gray-300 font-semibold text-sm rounded-full hover:border-amber-500/50 hover:text-white transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-building-2-line text-base"></i>
                Investment Info
              </Link>
            </div>
          </div>
        </motion.div>

        {/* ===== SECTION 2: Year-over-Year Growth ===== */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
          className="mb-3"
        >
          <p className="text-gray-600 text-[10px] font-semibold tracking-[0.15em] uppercase">Section 02 — Traffic & Visitor Growth Trends</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.15 }}
          className="bg-gray-900/80 border border-gray-800 rounded-lg p-7 mb-10 hover:border-amber-500/30 transition-colors duration-300"
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="text-white font-bold text-base mb-1">Annual Traffic & Visitor Trends</p>
              <p className="text-gray-500 text-xs">2019–2024 | Post-COVID Recovery & Growth</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-sm bg-amber-500"></div>
                <span className="text-gray-400 text-[10px]">AADT (K vehicles/day)</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-sm bg-cyan-400"></div>
                <span className="text-gray-400 text-[10px]">Visitors (M/year)</span>
              </div>
            </div>
          </div>

          {/* Chart area */}
          <div className="flex items-end gap-3 h-52 mb-4">
            {yearlyGrowthData.map((item, i) => (
              <div key={item.year} className="flex-1 flex flex-col items-center gap-1.5">
                <div className="w-full flex gap-1 items-end justify-center h-44">
                  {/* AADT bar */}
                  <motion.div
                    initial={{ height: 0 }}
                    whileInView={{ height: `${(item.aadt / 210) * 100}%` }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.2 + i * 0.08 }}
                    className="w-[40%] bg-gradient-to-t from-amber-600 to-amber-400 rounded-t-md relative group cursor-default"
                  >
                    <div className="absolute -top-6 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-gray-800 text-white text-[9px] px-1.5 py-0.5 rounded whitespace-nowrap">
                      {item.aadt}K
                    </div>
                  </motion.div>
                  {/* Visitors bar */}
                  <motion.div
                    initial={{ height: 0 }}
                    whileInView={{ height: `${(item.visitors / 45) * 100}%` }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.3 + i * 0.08 }}
                    className="w-[40%] bg-gradient-to-t from-cyan-600 to-cyan-400 rounded-t-md relative group cursor-default"
                  >
                    <div className="absolute -top-6 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-gray-800 text-white text-[9px] px-1.5 py-0.5 rounded whitespace-nowrap">
                      {item.visitors}M
                    </div>
                  </motion.div>
                </div>
                <span className={`text-[10px] font-mono ${item.year === '2024' ? 'text-amber-400 font-bold' : 'text-gray-500'}`}>{item.year}</span>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-gray-800">
            <p className="text-gray-600 text-[10px]">Source: Nevada DOT / LVCVA Annual Reports</p>
            <div className="flex items-center gap-4">
              <span className="text-gray-400 text-[10px]">AADT CAGR (2019→2024): <strong className="text-amber-400">+4.1%</strong></span>
              <span className="text-gray-400 text-[10px]">Visitor CAGR: <strong className="text-cyan-400">+5.8%</strong></span>
            </div>
          </div>
        </motion.div>

        {/* ===== SECTION 3: Event Peak Demand ===== */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
          className="mb-3"
        >
          <p className="text-gray-600 text-[10px] font-semibold tracking-[0.15em] uppercase">Section 03 — Major Event Impact</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.15 }}
          className="bg-gray-900/80 border border-gray-800 rounded-lg p-7 mb-10 hover:border-amber-500/30 transition-colors duration-300"
        >
          <div className="mb-6">
            <p className="text-white font-bold text-base mb-1">Peak Demand During Major Events</p>
            <p className="text-gray-500 text-xs">Major events drive I-15 traffic up to 3.4x normal levels, creating premium advertising opportunities with higher CPM rates.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {eventPeakData.map((event, i) => (
              <motion.div
                key={event.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: 0.2 + i * 0.08 }}
                className="bg-gray-800/50 border border-gray-700/50 rounded-md p-5 hover:border-amber-500/20 transition-colors duration-300"
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-white font-bold text-sm">{event.name}</span>
                  <span className="text-amber-400 text-[10px] font-medium bg-amber-500/10 px-2 py-0.5 rounded-full">{event.period}</span>
                </div>
                <div className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-500 mb-2">
                  {event.surge}
                </div>
                <p className="text-gray-400 text-[10px] mb-1">Traffic Surge (vs Normal)</p>
                <div className="pt-3 mt-3 border-t border-gray-700/50">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500 text-[10px]">Attendance</span>
                    <span className="text-gray-300 text-[10px] font-semibold">{event.attendees}</span>
                  </div>
                  <p className="text-gray-600 text-[10px] mt-1">{event.note}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <p className="text-gray-600 text-[10px] mt-5">Source: Official Event Data / LVCVA Event Impact Reports 2024 / Nevada DOT Special Counts</p>
        </motion.div>

        {/* ===== SECTION 4: CPM Comparison + Hourly Exposure ===== */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
          className="mb-3"
        >
          <p className="text-gray-600 text-[10px] font-semibold tracking-[0.15em] uppercase">Section 04 — Cost Efficiency &amp; 24/7 Exposure</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 mb-10">
          {/* CPM Comparison */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="lg:col-span-7 bg-gray-900/80 border border-gray-800 rounded-lg p-7 hover:border-amber-500/30 transition-colors duration-300"
          >
            <div className="mb-6">
              <p className="text-white font-bold text-base mb-1">CPM Comparison Across Media Channels</p>
              <p className="text-gray-500 text-xs">Billboard advertising delivers approximately 1/5 to 1/14 the cost per thousand impressions compared to digital channels.</p>
            </div>

            <div className="space-y-3.5">
              {cpmComparisonData.map((item, i) => (
                <div key={item.media}>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-semibold ${item.highlight ? 'text-emerald-400' : 'text-gray-300'}`}>{item.media}</span>
                      {item.highlight && (
                        <span className="text-[9px] bg-emerald-500/15 text-emerald-400 px-1.5 py-0.5 rounded font-semibold">BEST VALUE</span>
                      )}
                    </div>
                    <span className={`text-sm font-bold tabular-nums ${item.highlight ? 'text-emerald-400' : 'text-gray-400'}`}>${item.cpm.toFixed(2)}</span>
                  </div>
                  <div className="w-full h-2 bg-gray-800 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: `${item.barPercent}%` }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.8, delay: 0.3 + i * 0.1 }}
                      className={`h-full rounded-full ${item.color}`}
                    ></motion.div>
                  </div>
                  <p className="text-gray-600 text-[9px] mt-0.5">Source: {item.source}</p>
                </div>
              ))}
            </div>

            <div className="mt-5 pt-4 border-t border-gray-800">
              <p className="text-gray-600 text-[10px] leading-relaxed">
                * CPM figures represent 2024 industry averages. Billboard CPM calculated internally using AADT 205K × DEC factor × rotation schedule. Actual rates vary by contract terms.
              </p>
            </div>
          </motion.div>

          {/* Hourly Exposure + Brand Recall */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.25 }}
            className="lg:col-span-5 flex flex-col gap-4"
          >
            {/* Hourly Exposure */}
            <div className="bg-gray-900/80 border border-gray-800 rounded-lg p-7 hover:border-amber-500/30 transition-colors duration-300 flex-1">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-7 h-7 flex items-center justify-center rounded-md bg-amber-500/10">
                      <i className="ri-time-line text-amber-500 text-base"></i>
                    </div>
                    <span className="text-gray-500 text-[10px] font-semibold tracking-wider uppercase">24H Exposure Stability</span>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-500">92%</p>
                  <p className="text-gray-500 text-[10px]">Uptime Coverage</p>
                </div>
              </div>

              <div className="space-y-2">
                {hourlyData.map((slot, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <span className="text-gray-500 text-[10px] font-mono w-12 shrink-0">{slot.label}</span>
                    <div className="flex-1 h-5 bg-gray-800 rounded-md overflow-hidden relative">
                      <motion.div
                        initial={{ width: 0 }}
                        whileInView={{ width: `${slot.percent}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, delay: 0.4 + i * 0.08 }}
                        className="h-full rounded-md bg-gradient-to-r from-amber-500 to-orange-500"
                      ></motion.div>
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-bold text-gray-300">{slot.value}K</span>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-gray-600 text-[10px] mt-3">Maintains 68K+ vehicles/day even during overnight hours — Source: Nevada DOT Hourly Traffic Data</p>
            </div>

            {/* Media Value Summary */}
            <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-md bg-white/20 shrink-0">
                  <i className="ri-line-chart-line text-white text-xl"></i>
                </div>
                <div>
                  <p className="text-white/80 text-[10px] font-semibold uppercase tracking-wider mb-1">Estimated Media Value (Per Face)</p>
                  <p className="text-white text-2xl font-black mb-0.5">$2.80 CPM</p>
                  <p className="text-white/70 text-[10px]">Approximately <strong className="text-white">1/14th</strong> the cost of Google Search Ads</p>
                  <p className="text-white/50 text-[9px] mt-1">Internal Calculation | AADT & Geopath DEC Standard</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* ===== SECTION 5: Brand Recall Evidence ===== */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
          className="mb-3"
        >
          <p className="text-gray-600 text-[10px] font-semibold tracking-[0.15em] uppercase">Section 05 — Brand Recall &amp; Consumer Action</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.15 }}
          className="bg-gray-900/80 border border-gray-800 rounded-lg p-7 mb-10 hover:border-amber-500/30 transition-colors duration-300"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <p className="text-white font-bold text-base mb-1">OOH Advertising Effectiveness</p>
              <p className="text-gray-500 text-xs mb-5">Consumer behavior and recall metrics following OOH ad exposure</p>
              <p className="text-gray-600 text-[10px]">Source: OAAA / Harris Poll "OOH Advertising Study" 2024</p>
            </div>
            <div className="md:col-span-2 space-y-4">
              {[
                { label: 'Brand Awareness (Single Exposure)', value: 63, note: '63% recall ad after one viewing' },
                { label: 'Brand Recall (3+ Exposures)', value: 78, note: 'Significant lift with repeated exposure' },
                { label: 'Purchase Intent / Action Taken', value: 41, note: 'Includes online search and store visits' },
              ].map((item, i) => (
                <div key={i}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-gray-300 text-sm">{item.label}</span>
                    <span className="text-white font-bold text-sm">{item.value}%</span>
                  </div>
                  <div className="w-full h-2 bg-gray-800 rounded-full overflow-hidden mb-1">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: `${item.value}%` }}
                      viewport={{ once: true }}
                      transition={{ duration: 1, delay: 0.4 + i * 0.15 }}
                      className="h-full bg-gradient-to-r from-amber-500 to-orange-500 rounded-full"
                    ></motion.div>
                  </div>
                  <p className="text-gray-600 text-[10px]">{item.note}</p>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

      </div>
    </section>
  );
}
